import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { useNavigate, useRouter } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useState } from "react"
import { toast } from "sonner"

import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Input } from "@/components/Input/Input.tsx"
import { PromptInput } from "@/components/PromptInput/PromptInput.tsx"
import { PillToggleGroup } from "@/components/ToggleGroup/PillToggleGroup.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useGitHubProfile } from "@/hooks/useGitHubProfile.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useInvalidateQueries, useOrgContext, useQueries } from "@/hooks/useRootContext.ts"
import { GithubFill, Icon20, Icon32, JoystickRegular, SearchMedium } from "@/icons/Icons.ts"
import demoProjectCover from "@/illustrations/demo-project-cover.png"
import { formatErrorMessage } from "@/lib/displayUtils.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { projectsV2QueryKey } from "@/lib/queries.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { GitHubAccountSelector } from "@/routes/projects/github/GitHubAccountSelector.tsx"
import { GitHubRepositoryList } from "@/routes/projects/github/GitHubRepositoryList.tsx"
import { NewProjectGitHubIntegrationModel } from "@/routes/projects/github/NewProjectGitHubIntegrationModel.ts"
import { NewProjectFormModel } from "@/routes/projects/NewProjectFormModel.ts"
import { TaskNewSceneDevPanel } from "@/routes/tasks/TaskNewScene/TaskNewScene.tsx"
import * as textStyles from "@/styles/textStyles.ts"
import { baseFontSize, smallerFontSize } from "@/styles/textStyles.ts"

import type { AiAgentTask } from "@/lib/client/generated/TypeDefinitions.ts"
import type { ChangeEvent, MouseEvent } from "react"

export const NewProjectForm = observer(() => {
  const devMode = useDevMode()
  const context = useOrgContext()

  const queries = useQueries()
  const invalidateQueries = useInvalidateQueries()

  const navigate = useNavigate()
  const router = useRouter()

  const model = useModel(() => {
    const onComplete = (task: AiAgentTask) => {
      const redirectLocation = Navigator.task(context.orgId, task.projectKey, task.id, {})

      startViewTransition(() => navigate(redirectLocation))
    }

    return new NewProjectFormModel(context, invalidateQueries, router, onComplete)
  })

  const validationError = model.inputModel.validationError.get()
  const canCreateSampleProject = model.canCreateSampleProject.get()

  const tryDemo = useCallback(async (e: MouseEvent) => {
    e.preventDefault()

    try {
      const task = await queries.apiClient.createSampleProject(null)

      void invalidateQueries({ queryKey: projectsV2QueryKey })

      const redirectLocation = Navigator.task(context.orgId, task.projectKey, task.id, {})
      startViewTransition(() => navigate(redirectLocation))
    } catch (err) {
      toast.error(formatErrorMessage(err, "Failed to create sample project"))
    }
  }, [queries, invalidateQueries, context, navigate])

  return (
    <FormContainer>
      {devMode && (
        <PromptInputWithGitHubIntegration model={model} />
      )}
      {!devMode && (
        <PromptInput model={model.inputModel} singleLine />
      )}

      {validationError && (
        <ValidationError>
          {validationError}
        </ValidationError>
      )}

      {canCreateSampleProject && (
        <DemoButton href="#" onClick={tryDemo}>
          <JoystickRegular className={Icon32} color="var(--text-color)" />
          <CardTitle>Play with the demo project</CardTitle>
          <CardSubtitle>Try Matter by enhancing our postcard builder</CardSubtitle>
          <DemoProjectContainer>
            <DemoProjectTaskList>
              <DemoProjectTaskListItem />
            </DemoProjectTaskList>
            <DemoProjectIllustration url={demoProjectCover} backgroundColor="#E2FEFF" />
          </DemoProjectContainer>
        </DemoButton>
      )}

      {devMode && (
        <TaskNewSceneDevPanel model={model.taskSettings} />
      )}
    </FormContainer>
  )
})

type NewProjectFormMode = "repositories" | "url"

// TODO(dp): merge into NewProjectForm when GitHub integration is not under the devMode flag
const PromptInputWithGitHubIntegration = observer(({ model }: {
  model: NewProjectFormModel
}) => {
  const context = useOrgContext()

  const { isConnected, isCheckingConnection, isLoadingAccounts, accounts } = useGitHubProfile()

  const gitHubModel = useModel(() => {
    return new NewProjectGitHubIntegrationModel(context, model.inputModel)
  }, [context, model.inputModel])

  const selectedAccountLogin = gitHubModel.selectedAccountLogin.get()
  const selectedAccount = accounts.find(a => a.login === selectedAccountLogin)

  // Auto-select first account when accounts become available
  useEffect(() => {
    if (accounts.length > 0 && selectedAccountLogin === null) {
      gitHubModel.setSelectedAccountLogin(accounts[0].login)
    }
  }, [accounts, selectedAccountLogin, gitHubModel])

  const searchQuery = gitHubModel.searchQuery.get()
  const repositories = gitHubModel.repositories.get()
  const isLoadingRepositories = gitHubModel.isLoadingRepositories.get()

  const [formMode, setFormMode] = useState<NewProjectFormMode>("repositories")
  const formModes: NewProjectFormMode[] = ["repositories", "url"]

  const isCheckingConnectionStatus = isConnected === null || isCheckingConnection
  const isInitialLoad = repositories.length === 0 && (isLoadingRepositories || isCheckingConnectionStatus)

  // Load repositories when account or search query changes
  useEffect(() => {
    if (isConnected === true && selectedAccount) {
      gitHubModel.resetRepositories()
      void gitHubModel.loadRepositories(selectedAccount)
    }
  }, [selectedAccount?.login, searchQuery, isConnected, gitHubModel]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleImportFromGitHub = useCallback(async (e: MouseEvent) => {
    e.preventDefault()
    await gitHubModel.handleImportFromGitHub()
  }, [gitHubModel])

  if (isCheckingConnectionStatus) {
    return <LoadingConnectionContainer />
  }

  if (!isConnected) {
    return (
      <>
        <PromptInput model={model.inputModel} singleLine />

        <GitHubButton href="#" onClick={handleImportFromGitHub}>
          <Vertical gap="4px" alignItems="center">
            <GithubFill className={Icon32} color="var(--text-color)" />
            <CardTitle>Import from GitHub</CardTitle>
            <CardSubtitle>Mirror your code into a safe workspace and start building</CardSubtitle>
          </Vertical>
        </GitHubButton>
      </>
    )
  }

  return (
    <>
      <ToggleGroupContainer>
        <PillToggleGroup
          items={formModes}
          selectedItem={formMode}
          onItemChange={setFormMode}
          getKey={(item) => item}
          getValue={(item) => item === "repositories" ? "Your repositories" : "Enter URL"}
        />
      </ToggleGroupContainer>

      {formMode === "repositories" && (
        <>
          {isLoadingAccounts ? (
            <RepositoryFiltersContainer>
              <AccountSelectorSkeleton />
              <SearchBarSkeleton />
            </RepositoryFiltersContainer>
          ) : accounts && accounts.length > 0 && (
            <RepositoryFiltersContainer>
              <GitHubAccountSelector
                accounts={accounts}
                selectedAccount={selectedAccount}
                onAccountChange={gitHubModel.setSelectedAccountLogin}
              />
              <RepositorySearchBar
                searchQuery={gitHubModel.searchQuery.get()}
                onSearchChange={gitHubModel.setSearchQueryDebounced}
              />
            </RepositoryFiltersContainer>
          )}

          {isInitialLoad ? (
            <RepositoryListContainer>
              {Array.from({ length: 3 }).map((_, i) => (
                <RepositorySkeletonCard key={i}>
                  <SkeletonContent>
                    <SkeletonAvatar />
                    <SkeletonName />
                  </SkeletonContent>
                  <SkeletonButton />
                </RepositorySkeletonCard>
              ))}
            </RepositoryListContainer>
          ) : repositories && repositories.length > 0 ? (
            <GitHubRepositoryList
              repositories={repositories}
              hasMore={gitHubModel.hasMoreRepositories.get()}
              onRepositorySelect={(repo) => gitHubModel.handleRepositorySelect(repo)}
              onShowMore={() => {
                void gitHubModel.loadMoreRepositories(selectedAccount)
              }}
            />
          ) : !isLoadingAccounts && (
            <>
              <EmptyRepositoriesState>
                No repositories found. Clear filters or switch to "Enter URL".
              </EmptyRepositoriesState>
            </>
          )}
        </>
      )}

      {formMode === "url" && (
        <PromptInput model={model.inputModel} singleLine />
      )}
    </>
  )
})


const RepositorySearchBar = ({ searchQuery, onSearchChange }: {
  searchQuery: string
  onSearchChange: (query: string) => void
}) => {
  // TODO(dp): use the approach similar to DropdownFilterInput instead
  const [localQuery, setLocalQuery] = useState(searchQuery)

  useEffect(() => {
    setLocalQuery(searchQuery)
  }, [searchQuery])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalQuery(value)
    onSearchChange(value)
  }

  return (
    <SearchInputContainer>
      <SearchIcon>
        <SearchMedium className={Icon20} />
      </SearchIcon>
      <SearchInput
        type="text"
        placeholder="Search"
        value={localQuery}
        onChange={handleChange}
      />
    </SearchInputContainer>
  )
}

const FormContainer = styled(Vertical)`
  gap: 16px;
  width: 100%;
`

const ToggleGroupContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

const RepositoryFiltersContainer = styled(Horizontal)`
  gap: 8px;
  align-items: center;
`

const SearchInputContainer = styled(Horizontal)`
  flex: 1;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  height: 40px;
  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-12);
  background-color: var(--mask-color);
`

const SearchIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--secondary-text-color);
  flex-shrink: 0;
`

const SearchInput = styled(Input)`
  border: none;
  padding: 0;
  background: transparent;

  &:focus-visible {
    outline: none;
  }

  &:hover:not(:disabled, :focus-visible) {
    background: transparent;
  }
`

const RepositoryListContainer = styled(Vertical)`
  gap: 8px;
  width: 100%;
`

const RepositorySkeletonCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-8);
  background-color: var(--mask-color);
  gap: 12px;
  position: relative;
  overflow: hidden;
`

const SkeletonAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--text-color-3);
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`

const SkeletonContent = styled(Horizontal)`
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const SkeletonText = styled.div`
  height: 14px;
  border-radius: 4px;
  background: var(--text-color-3);
  position: relative;
  overflow: hidden;
`

const SkeletonName = styled(SkeletonText)`
  width: 100px;
`

const SkeletonButton = styled(SkeletonText)`
  width: 80px;
  height: 24px;
  border-radius: var(--border-radius-8);
  flex-shrink: 0;
`

const AccountSelectorSkeleton = styled.div`
  height: 40px;
  width: 145px;
  border-radius: var(--border-radius-8);
  background-color: var(--text-color-3);
  position: relative;
  overflow: hidden;

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }

    100% {
      transform: translateX(100%);
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgb(255 255 255 / 60%) 50%, transparent 100%);
    animation: shimmer 1.5s infinite;
    will-change: transform;
  }
`

const SearchBarSkeleton = styled.div`
  flex: 1;
  height: 40px;
  border-radius: var(--border-radius-20);
  background-color: var(--text-color-3);
  position: relative;
  overflow: hidden;

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }

    100% {
      transform: translateX(100%);
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgb(255 255 255 / 60%) 50%, transparent 100%);
    animation: shimmer 1.5s infinite;
    will-change: transform;
  }
`

const LoadingConnectionContainer = styled.div`
  height: 100px;
  width: 100%;
  border-radius: var(--border-radius-8);
  background-color: var(--text-color-3);
  position: relative;
  overflow: hidden;

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }

    100% {
      transform: translateX(100%);
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgb(255 255 255 / 60%) 50%, transparent 100%);
    animation: shimmer 1.5s infinite;
    will-change: transform;
  }
`

const EmptyRepositoriesState = styled.div`
  text-align: center;
  color: var(--secondary-text-color);
  font-size: var(--small-font-size);
  padding: 24px;
  border: 1px dashed var(--separator-color);
  border-radius: var(--border-radius-8);
`

const ValidationError = styled.span`
  ${smallerFontSize};

  color: var(--error-color)
`

const ActionCard = styled.a`
  position: relative;

  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  max-width: 480px;
  border: 1px solid var(--text-color-10);
  border-radius: var(--border-radius-16);
  box-shadow: 0 2px 16px 0 oklch(37.6% 0.183 267.561deg / 5%);

  color: var(--text-color);
  cursor: pointer;
  text-decoration: none;
  transition: scale 0.3s ease-in-out;
  z-index: 1;

  &::before {
    content: "";

    position: absolute;
    inset: 0;

    opacity: 0;
    border-radius: var(--border-radius-16);
    transition: opacity 0.3s linear;
    pointer-events: none;
    z-index: 0;
  }

  &:hover {
    border: 1px solid var(--text-color-15);
    scale: 1.001;
    box-shadow: 0 2px 20px 0 oklch(37.6% 0.183 267.561deg / 10%);

    &::before {
      opacity: 1;
    }
  }
`

const GitHubButton = styled(ActionCard)`
  gap: 16px;
  padding: 16px 24px;

  background: radial-gradient(110% 145% at 100% -95%, rgb(156 123 255 / 20%) 0%, rgb(243 75 255 / 0%) 100%), radial-gradient(50% 65% at -10% 90%, rgb(123 153 255 / 20%) 0%, rgb(129 75 255 / 0%) 100%), var(--background-color);
  background-clip: padding-box;

  &::before {
    background: radial-gradient(180% 200% at 100% -95%, rgb(255 123 178 / 10%) 0%, rgb(243 75 255 / 0%) 100%), radial-gradient(70% 80% at -10% 90%, rgb(123 147 255 / 10%) 0%, rgb(129 75 255 / 0%) 100%);
  }
`

// -- DemoButton (Play with the demo project)

const DemoButton = styled(ActionCard)`
  gap: 0;
  height: 100%;
  padding: 16px 24px 0;
  overflow: hidden;

  background: radial-gradient(80% 70% at 0% -10%, rgb(222 254 255) 0%, rgb(226 254 255 / 0%) 100%), #FFF;
  background-clip: padding-box;

  &::before {
    background: radial-gradient(100% 160% at 8% 4%, rgb(232 253 255) 0%, rgb(226 242 255 / 0%) 100%);
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    transition: scale 0.1s ease-in-out;
  }
`

const CardTitle = styled.div`
  ${baseFontSize};
  font-weight: 500;
  color: var(--text-color);
`

const CardSubtitle = styled.div`
  ${smallerFontSize};
  text-align: center;
  font-weight: 400;
  width: clamp(100px, 60%, 300px);
  color: var(--secondary-text-color);
`

// -- DemoProjectTaskListItem (small white card behind)

const DemoProjectContainer = styled.div`
  position: relative;
  width: 300px;
  height: 140px;
  margin-top: 8px;
`

const DemoProjectTaskList = styled.div`
  position: relative;
  transform: translateY(-10px);
  transition: transform 0.3s ease;
  z-index: 0;

  ${DemoButton}:hover & {
    transform: translateY(-5px);
  }
`

const DemoProjectTaskListItem = styled.div`
  position: absolute;
  left: 50%;

  width: 180px;
  height: 92px;

  margin-top: 20px;
  margin-left: -108px;
  padding: 4px;

  border: 4px solid oklch(100% 0 90deg);
  border-radius: var(--border-radius-8);

  background: var(--background-color);
  box-shadow: 0 4px 14px 0 oklch(0% 0 0deg / 15%);

  transition: transform 0.3s ease;

  color: oklch(39.5% 0.001 17.215deg / 20%);
  ${textStyles.smallFontSize};
  line-height: 140%;

  z-index: 0;
  transform: rotate(5.667deg);

  ${DemoButton}:hover & {
    transform: rotate(8deg) translateY(-5px);
  }
`

// -- DemoProjectIllustration (cropped centered postcard image)

const ProjectCoverRoot = styled(Horizontal)`
  position: relative;
  z-index: 0;

  margin-top: 20px;
  padding: 6px;

  background: var(--background-color);
  border-radius: var(--border-radius-12);

  --preview-shadow: 15%;
  box-shadow: 0 4px 14px 0 rgb(0 0 0 / var(--preview-shadow));

  transition: transform 0.3s ease;

  ${DemoButton}:hover & {
    transform: translateY(5px);
  }
`

const ProjectCoverWrap = styled.div<{ backgroundColor?: string }>`
  position: relative;
  overflow: hidden;
  flex: 1;

  width: 260px;
  height: 180px;
  background: ${props => props.backgroundColor || "#FFF"};

  border-radius: var(--border-radius-10);

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`

const InvisibleImage = css`
  visibility: hidden;
`

const DemoProjectIllustration = (props: { url?: string | null; backgroundColor?: string }) => {
  const [loadingError, setLoadingError] = useState(false)

  return (
    <ProjectCoverRoot>
      <ProjectCoverWrap backgroundColor={props.backgroundColor}>
        {props.url && (
          <img
            src={props.url}
            alt="Project cover"
            onError={() => setLoadingError(true)}
            className={cx(loadingError ? InvisibleImage : null)}
          />
        )}
      </ProjectCoverWrap>
    </ProjectCoverRoot>
  )
}
